import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { getActiveTenantId } from "@/shared/tenant/active-tenant";
import { ArrowRight, Sparkles, MapPin, Compass, X } from "lucide-react";
import { cn } from "@/shared/lib/utils";
import { Spark } from "@/shared/components/brand/Spark";
import { Button } from "@/shared/components/ui/button";
import { InfoHint } from "@/shared/components/ui/info-hint";
import { Card } from "@/shared/components/ui/card";
import { StatusText } from "@/shared/components/ui/status-chip";
import { TableCard, TableHeadRow, TableRow, Th, Empty } from "@/shared/components/ui/table";
import { EmptyState } from "@/shared/components/ui/empty-state";
import { useRunDiscovery, useDiscoveryRuns } from "../use-discovery";
import { useCampaignDetail } from "../use-campaign";
import { useConnectors } from "../use-connectors";
import type { DiscoverySource, DiscoveryRunSummary } from "../types";

const GRID = "grid-cols-[3fr_1fr_1fr_3fr]";
const MIN_PROMPT = 3;
const MAX_LIMIT = 25;

const isAbort = (e: unknown): boolean =>
  !!e && (((e as { name?: string }).name === "AbortError") || /abort/i.test((e as { message?: string }).message ?? ""));

// Only the three real connector keys are valid sources; anything else falls back to AI
// (a persisted lastSource is server-validated, but never trust it blindly on the hot path).
const asSource = (x: unknown): DiscoverySource =>
  x === "PLACES" || x === "AIRDNA" || x === "AI" ? x : "AI";

/**
 * Discovery (ADR-0023) — run a discovery pass for this campaign and see the candidates,
 * screened live for dedup/suppression.
 *
 * The brief is the campaign's own persisted field: we read it back from the campaign
 * and pre-fill the command bar on EVERY visit (reload, deep-link, days later), so the
 * user never retypes it. Arriving straight from New Campaign (`state.autostart`) kicks
 * off one run automatically — visibly, and cancellable. Editing the prompt and running
 * again updates the campaign's brief server-side (last-wins).
 */
export function DiscoveryTab() {
  const { id: campaignId } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const st = location.state as { autostart?: boolean } | null;

  const campaign = useCampaignDetail(campaignId ?? "");
  const runsQuery = useDiscoveryRuns(campaignId ?? "");

  const [prompt, setPrompt] = useState("");
  const [source, setSource] = useState<DiscoverySource>("AI");
  const [limit, setLimit] = useState(10);
  const [cancelled, setCancelled] = useState(false);
  const dirty = useRef(false); // the user has edited the prompt — stop seeding from the server
  const seeded = useRef(false);
  const autoRan = useRef(false);
  const abortRef = useRef<AbortController | null>(null);

  const run = useRunDiscovery(campaignId ?? "");
  const connectors = useConnectors();
  const available = (k: DiscoverySource) =>
    (connectors.data ?? []).find((c) => c.key === k)?.available ?? (k === "AI");

  // Seed the command bar from the PERSISTED campaign brief/source once it loads (unless
  // the user already started editing). This is what makes a revisit pre-filled.
  useEffect(() => {
    if (seeded.current || dirty.current || !campaign.data) return;
    if (campaign.data.brief) setPrompt(campaign.data.brief);
    setSource(asSource(campaign.data.lastSource));
    seeded.current = true;
  }, [campaign.data]);

  // Auto-run ONCE, only when arriving straight from New Campaign (autostart), using the
  // persisted brief directly (avoids racing the prompt state update). We then CLEAR the
  // flag from history state — otherwise a reload or Back to this entry would re-fire a
  // billable run, because `autostart` persists in window.history.state across mounts.
  useEffect(() => {
    if (autoRan.current || !st?.autostart || !campaignId) return;
    const brief = campaign.data?.brief?.trim() ?? "";
    if (brief.length < MIN_PROMPT) return;
    autoRan.current = true;
    navigate(location.pathname, { replace: true, state: null }); // consume the one-shot flag
    runWith(brief, asSource(campaign.data?.lastSource), 10);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaign.data?.brief, st?.autostart, campaignId]);

  const canRun = prompt.trim().length >= MIN_PROMPT && !run.isPending && !!campaignId;

  function runWith(p: string, s: DiscoverySource, l: number) {
    const controller = new AbortController();
    abortRef.current = controller;
    setCancelled(false);
    run.mutate({ prompt: p.trim(), limit: l, source: s, signal: controller.signal });
  }
  function start() {
    if (canRun) runWith(prompt, source, limit);
  }
  // Cancel aborts the client's wait, but the server run may already have landed (Nest
  // doesn't observe the disconnect), so we refresh the queries that a completed run would
  // have touched — if it did finish, its leads/history/brief show up.
  function cancel() {
    abortRef.current?.abort();
    setCancelled(true);
    if (campaignId) {
      qc.invalidateQueries({ queryKey: ["leads", getActiveTenantId(), campaignId] });
      qc.invalidateQueries({ queryKey: ["campaign", getActiveTenantId(), campaignId] });
      qc.invalidateQueries({ queryKey: ["campaigns"] });
      qc.invalidateQueries({ queryKey: ["discovery-runs", getActiveTenantId(), campaignId] });
      qc.invalidateQueries({ queryKey: ["discovery-runs-feed", getActiveTenantId()] });
    }
  }

  const result = run.data && !run.isPending ? run.data : undefined;
  const showError = run.isError && !isAbort(run.error);

  return (
    <div className="space-y-6">
      {/* Command bar */}
      <Card className="p-[18px]">
        <div className="flex gap-3">
          <Spark size={16} className="mt-1 shrink-0" />
          <textarea
            data-testid="discovery-prompt"
            rows={2}
            value={prompt}
            onChange={(e) => {
              dirty.current = true;
              setPrompt(e.target.value);
            }}
            placeholder="e.g. Independent strength gyms and CrossFit boxes in Austin, TX with strong reviews"
            className="w-full resize-none bg-transparent text-[15px] leading-[1.5] text-ge-primary placeholder:text-ge-muted focus-visible:outline-none"
          />
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <SourceToggle source={source} onChange={setSource} available={available} />
          <label className="flex items-center gap-2 rounded-ge-param border border-ge-border-card px-2 py-1 font-mono text-[11px] text-ge-secondary">
            limit
            <input
              data-testid="discovery-limit"
              type="number"
              min={1}
              max={MAX_LIMIT}
              value={limit}
              onChange={(e) =>
                setLimit(Math.max(1, Math.min(MAX_LIMIT, Number(e.target.value) || 1)))
              }
              className="w-10 bg-transparent text-ge-value focus-visible:outline-none"
            />
          </label>
          <div className="ml-auto flex items-center gap-2">
            {run.isPending && (
              <Button variant="outline" size="sm" data-testid="cancel-discovery" onClick={cancel}>
                <X className="h-[14px] w-[14px]" strokeWidth={2.4} />
                Stop
              </Button>
            )}
            <Button data-testid="run-discovery" disabled={!canRun} onClick={start}>
              {run.isPending ? "Discovering…" : "Run discovery"}
              <ArrowRight className="h-[14px] w-[14px]" strokeWidth={2.4} />
            </Button>
          </div>
        </div>
        {showError && (
          <div className="mt-2 flex items-center gap-3" data-testid="discovery-error">
            <p className="text-[12px] text-ge-danger">
              Discovery failed. Check the source is configured and try again.
            </p>
            <Button variant="outline" size="sm" data-testid="retry-discovery" onClick={start} disabled={!canRun}>
              Retry
            </Button>
          </div>
        )}
        {cancelled && !run.isPending && !result && (
          <p className="mt-2 text-[12px] text-ge-muted" data-testid="discovery-cancelled">
            Stopped waiting. The run may still finish in the background — its leads will
            appear in the Leads tab and run history if it does.
          </p>
        )}
      </Card>

      {/* Results */}
      {run.isPending && (
        <p className="font-mono text-[12px] text-ge-muted" data-testid="discovery-running">
          Running discovery and screening candidates… you can cancel above.
        </p>
      )}

      {result && (
        <div className="space-y-4" data-testid="discovery-result">
          <div>
            <p className="ge-eyebrow flex items-center gap-1">BRIEF <InfoHint text="The model's interpretation of your prompt — what it understood you're looking for. Check it matches your intent before approving leads." /></p>
            <p className="mt-1 text-[13.5px] text-ge-secondary">{result.brief}</p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <SummaryChip label="candidates" value={result.screening.candidates} tone="accent" />
            <SummaryChip label="duplicates" value={result.screening.duplicates} tone="warning" />
            <SummaryChip label="suppressed" value={result.screening.suppressed} tone="danger" />
            <span className="ml-auto font-mono text-[11px] text-ge-faint">
              {result.model} · {result.usage.inputTokens}/{result.usage.outputTokens} tok
            </span>
          </div>

          {result.leads.length === 0 ? (
            <div data-testid="discovery-zero">
              <EmptyState message="No leads matched this brief. Edit the brief above, raise the limit, or switch source — then run again." />
            </div>
          ) : (
            <TableCard>
              <TableHeadRow grid={GRID}>
                <Th className="col-span-1">Name</Th>
                <Th>Rating</Th>
                <Th>Status</Th>
                <Th className="col-span-1">Why</Th>
              </TableHeadRow>
              {result.leads.map((v) => {
                const screenedOut = v.status === "DUPLICATE" || v.status === "SUPPRESSED";
                return (
                  <TableRow
                    key={v.id}
                    grid={GRID}
                    data-testid={`discovered-lead-${v.id}`}
                    className={cn(screenedOut && "opacity-[.55]")}
                  >
                    <div className="font-sans text-[13.5px] font-semibold text-ge-primary">
                      {v.name}
                      {v.website && (
                        <span className="ml-2 font-mono text-[11px] text-ge-faint">{v.website}</span>
                      )}
                    </div>
                    <div className="font-mono text-[11.5px] text-ge-value">
                      {v.rating != null ? v.rating.toFixed(1) : <Empty />}
                    </div>
                    <div>
                      <StatusText tone={statusTone(v.status)}>{v.status}</StatusText>
                    </div>
                    <div className="font-mono text-[11px] text-ge-secondary">
                      {v.screenReason ?? <Empty />}
                    </div>
                  </TableRow>
                );
              })}
            </TableCard>
          )}

          <p className="text-[12px] text-ge-muted">
            Screening is visible, not hidden: duplicates and already-registered gyms stay listed with
            their reason. Approve the candidates in the Leads tab.
          </p>
        </div>
      )}

      {!result && !run.isPending && !showError && !cancelled && (
        <EmptyState message="Run discovery from the command bar (AI or Google Places). Every run stores the exact request + token usage and is screened for dedup and suppression before landing as leads." />
      )}

      {/* Run history (ADR-0023) — answers "did I already search this?" */}
      <RunHistory runs={runsQuery.data} />
    </div>
  );
}

function RunHistory({ runs }: { runs?: DiscoveryRunSummary[] }) {
  if (!runs || runs.length === 0) return null;
  return (
    <div data-testid="run-history">
      <p className="ge-eyebrow flex items-center gap-1">RUN HISTORY <InfoHint text="Every discovery run on this campaign — prompt, source and how many candidates/duplicates/suppressed it produced." /></p>
      <Card className="mt-2 divide-y divide-ge-border-row">
        {runs.map((r) => (
          <div key={r.id} className="flex items-center gap-3 px-4 py-2.5">
            <span className="min-w-0 flex-1 truncate text-[13px] text-ge-primary">{r.prompt}</span>
            {r.source && <span className="font-mono text-[10.5px] text-ge-faint">{r.source}</span>}
            <span className="font-mono text-[11px] text-ge-accent">{r.candidates ?? 0} found</span>
            <span className="font-mono text-[10.5px] text-ge-muted">
              {new Date(r.createdAt).toLocaleDateString()}
            </span>
          </div>
        ))}
      </Card>
    </div>
  );
}

function statusTone(s: string): "accent" | "value" | "warning" | "danger" | "faint" {
  if (s === "DUPLICATE") return "warning";
  if (s === "SUPPRESSED") return "danger";
  if (s === "CANDIDATE") return "faint";
  return "value";
}

function SourceToggle({
  source,
  onChange,
  available,
}: {
  source: DiscoverySource;
  onChange: (s: DiscoverySource) => void;
  available: (s: DiscoverySource) => boolean;
}) {
  return (
    <div className="inline-flex rounded-ge-param border border-ge-border-card p-[2px]">
      <ToggleButton active={source === "AI"} disabled={!available("AI")} onClick={() => onChange("AI")}>
        <Sparkles className="h-[13px] w-[13px]" strokeWidth={1.8} /> AI
      </ToggleButton>
      <ToggleButton active={source === "PLACES"} disabled={!available("PLACES")} onClick={() => onChange("PLACES")}>
        <MapPin className="h-[13px] w-[13px]" strokeWidth={1.8} /> Places
      </ToggleButton>
      <ToggleButton active={source === "AIRDNA"} disabled={!available("AIRDNA")} onClick={() => onChange("AIRDNA")}>
        <Compass className="h-[13px] w-[13px]" strokeWidth={1.8} /> AirDNA
      </ToggleButton>
    </div>
  );
}

function ToggleButton({
  active,
  disabled,
  onClick,
  children,
}: {
  active: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={active}
      className={cn(
        "inline-flex items-center gap-1 rounded-[6px] px-2 py-1 font-mono text-[11px] transition-colors",
        active ? "bg-ge-accent-tint text-ge-accent" : "text-ge-secondary hover:text-ge-value",
        disabled && "cursor-not-allowed opacity-40 hover:text-ge-secondary",
      )}
    >
      {children}
    </button>
  );
}

function SummaryChip({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: "accent" | "warning" | "danger";
}) {
  return (
    <span
      className={cn(
        "rounded-ge-pill border px-[11px] py-[5px] font-mono text-[11px]",
        tone === "accent"
          ? "border-ge-border-accent bg-ge-accent-tint text-ge-accent"
          : tone === "warning"
            ? "border-dashed border-ge-border-warn text-ge-warning"
            : "border-dashed border-ge-border-danger text-ge-danger",
      )}
    >
      {label} {value}
    </span>
  );
}
